import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, CheckCircle2, Clock, Check, AlertCircle } from 'lucide-react';
import { BakeryEvent } from '../types/event';
import { INFO_EMAIL, sendBokabordMail } from '../utils/infoMail';
import { createBookingNumber, saveEventBooking } from '../utils/eventSheet';
import { formatPhone, validateEmail, validateName, validatePhone } from '../utils/validation';
interface EventSignupModalProps {
  event: BakeryEvent;
  onClose: () => void;
}
type Field = 'firstName' | 'lastName' | 'email' | 'phone';
const MAX_CHILDREN = 10;
const ease = [0.23, 1, 0.32, 1] as const;
export function EventSignupModal({
  event,
  onClose
}: EventSignupModalProps) {
  // Children's events: a parent ticks "Jag är förälder" and states how many
  // children they bring. Without the tick, the child signs up themselves.
  const isKidsEvent = !!event.childSignup || !!event.parentToggle;
  const [asParent, setAsParent] = useState(false);
  const isParent = isKidsEvent && asParent;
  const [childCount, setChildCount] = useState('1');
  const [childTouched, setChildTouched] = useState(false);
  const childNum = Number(childCount);
  const childError = !isParent ? null : childCount.trim() === '' ? 'Ange hur många barn du tar med.' : !Number.isInteger(childNum) || childNum < 1 ? 'Ange minst 1 barn.' : childNum > MAX_CHILDREN ? `Max ${MAX_CHILDREN} barn per anmälan – hör av dig till oss om ni är fler.` : null;
  const stepChildren = (delta: number) => {
    setChildTouched(true);
    setChildCount(c => {
      const current = c.trim() === '' || isNaN(Number(c)) ? 1 - delta : Number(c);
      return String(Math.min(MAX_CHILDREN, Math.max(1, current + delta)));
    });
  };
  const [values, setValues] = useState<Record<Field, string>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  const [touched, setTouched] = useState<Record<Field, boolean>>({
    firstName: false,
    lastName: false,
    email: false,
    phone: false
  });
  const [choice, setChoice] = useState<string | null>(null);
  const [choiceTouched, setChoiceTouched] = useState(false);
  const choiceError = event.bookingOptions && !choice ? 'Välj ett alternativ.' : null;
  const [party, setParty] = useState('');
  const [partyTouched, setPartyTouched] = useState(false);
  const selectedChoice = event.bookingOptions?.choices.find(c => c.label === choice);
  const partyCfg = selectedChoice?.partySize ?? event.partySize;
  const partyNum = Number(party);
  const partyError = !partyCfg ? null : party.trim() === '' ? 'Ange hur många ni är.' : !Number.isInteger(partyNum) ? 'Ange ett helt antal personer.' : partyNum < partyCfg.min ? partyCfg.minMessage ?? `Ni behöver vara minst ${partyCfg.min} personer för att boka.` : partyNum > partyCfg.max ? `Max ${partyCfg.max} personer per bokning – hör av dig till oss för större sällskap.` : null;
  const stepParty = (delta: number) => {
    setPartyTouched(true);
    setParty(p => {
      if (!partyCfg) return p;
      const current = p.trim() === '' || isNaN(Number(p)) ? partyCfg.min - delta : Number(p);
      return String(Math.min(partyCfg.max, Math.max(partyCfg.min, current + delta)));
    });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const errors: Record<Field, string | null> = {
    firstName: validateName(values.firstName, 'förnamn'),
    lastName: validateName(values.lastName, 'efternamn'),
    email: validateEmail(values.email),
    phone: validatePhone(values.phone)
  };
  const isValid = Object.values(errors).every(e => e === null);
  const dateLabel = new Date(`${event.date}T12:00:00`).toLocaleDateString('sv-SE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  });
  const firstName = values.firstName.trim();
  const lastName = values.lastName.trim();
  const fullName = `${firstName} ${lastName}`;
  const childText = `${childNum} barn`;
  const setField = (field: Field) => (e: React.ChangeEvent<HTMLInputElement>) => setValues(v => ({
    ...v,
    [field]: e.target.value
  }));
  const touch = (field: Field) => () => setTouched(t => ({
    ...t,
    [field]: true
  }));
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setChildTouched(true);
    setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true
    });
    setChoiceTouched(true);
    setPartyTouched(true);
    if (choiceError) {
      document.getElementById('su-choice-0')?.focus();
      if (isValid) return;
    }
    if (partyError) {
      document.getElementById('su-party')?.focus();
      if (isValid) return;
    }
    if (childError) {
      document.getElementById('su-children')?.focus();
      return;
    }
    if (!isValid) {
      const firstInvalid = (Object.keys(errors) as Field[]).find(f => errors[f]);
      if (firstInvalid) document.getElementById(`su-${firstInvalid}`)?.focus();
      return;
    }
    setIsSubmitting(true);
    const email = values.email.trim();
    const phone = formatPhone(values.phone);
    const timeText = selectedChoice?.time ?? `${event.time ?? 'Ej angiven'}${event.secondaryTime ? ` (${event.secondaryTime})` : ''}`;
    const bookingNumber = createBookingNumber(event.id);
    const choiceLabel = ({
      'halloween-run-club': 'DELTAR I:  ',
      'halloween-hidden-reality': 'SPELFORM:  ',
      'halloween-hostbrunch': 'SITTNING:  '
    } as Record<string, string>)[event.id] ?? 'VAL:       ';
    const message = [`════════════════════════════════`, `   NY BOKNING – HALLOWEEK`, `   Gäller: ${event.title}`, `   ${bookingNumber}`, `════════════════════════════════`, '', `▸ DEL:        Halloweek (vecka 44)`, `▸ EVENT:      ${event.title}`, `▸ DATUM:      ${event.date} (${dateLabel})`, `▸ TID:        ${timeText}`, ...(choice ? [`▸ ${choiceLabel} ${choice.toUpperCase()}`] : []), ...(partyCfg ? [`▸ SÄLLSKAP:   ${partyNum} personer`] : []), '', ...(isKidsEvent ? [`▸ ANMÄLD AV:  ${isParent ? 'FÖRÄLDER' : 'BARNET SJÄLV'}`, `▸ ANTAL BARN: ${isParent ? childNum : 1}`, ''] : []), `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, isParent ? `   FÖRÄLDER / KONTAKTPERSON` : `   DELTAGARE`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  Namn:      ${fullName}`, `  E-post:    ${email}`, `  Telefon:   ${phone}`, '', `════════════════════════════════`, isParent ? `Svara direkt på detta mejl för att nå föräldern.` : `Svara direkt på detta mejl för att nå deltagaren.`, `════════════════════════════════`].join('\n');

    // Sheet and email run independently – one failing never blocks the other
    const mail = {
      subject: `[HALLOWEEK · ${event.title}] ${event.date}${choice ? ` · ${choice}` : ''}${partyCfg ? ` – ${partyNum} pers` : ''} – ${isParent ? `Förälder: ${fullName} – ${childText}` : isKidsEvent ? `${fullName} (anmäler sig själv)` : fullName} (${bookingNumber})`,
      message,
      fromName: `Top Notch Halloweek – ${fullName}`,
      replyTo: email
    };
    const sheetBase = {
      eventId: event.id,
      bookingNumber,
      date: event.date,
      time: timeText,
      choice,
      partySize: partyCfg ? partyNum : null,
      firstName,
      lastName,
      email,
      phone,
      registeredBy: isKidsEvent ? isParent ? 'Förälder' : 'Barnet själv' : undefined,
      childCount: isKidsEvent ? isParent ? childNum : 1 : undefined
    };
    const [sheetOk, bokabordOk] = await Promise.all([saveEventBooking(sheetBase), sendBokabordMail(mail)]);
    if (!sheetOk) console.error('SheetDB: bokningen kunde inte sparas');
    if (!bokabordOk) console.error('Web3Forms: mejlet till bokabord@ kunde inte skickas');
    const ok = sheetOk || bokabordOk;
    setIsSubmitting(false);
    if (ok) {
      setSent(true);
    } else {
      setError(`Något gick fel. Försök igen eller mejla oss på ${INFO_EMAIL}.`);
    }
  };
  const renderField = (field: Field, label: string, props: React.InputHTMLAttributes<HTMLInputElement>) => {
    const showError = touched[field] && errors[field];
    const showValid = touched[field] && !errors[field];
    return <div data-id="element-491" data-mp="13oif5lx9q5yy">
        <label htmlFor={`su-${field}`} className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-492" data-mp="13orvsiwfb0rz">
          {label}
        </label>
        <div className="relative" data-id="element-493" data-mp="113ke1yzes9q0">
          <input id={`su-${field}`} value={values[field]} onChange={setField(field)} onBlur={touch(field)} aria-invalid={!!showError} aria-describedby={showError ? `su-${field}-error` : undefined} className={`w-full h-11 pl-3 pr-9 rounded-lg bg-[#0E0C0B] text-[#F5EDE3] placeholder:text-[#F5EDE3]/30 text-sm outline-none border transition-[border-color,box-shadow] duration-150 focus:ring-2 ${showError ? 'border-[#F87171] focus:ring-[#F87171]/25' : 'border-[#F5EDE3]/15 focus:border-[#FF7A1A] focus:ring-[#FF7A1A]/25'}`} {...props} data-id="element-494" data-mp="112j3dvmb1p8t" />
          {showValid && <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FF9A4D]" aria-hidden="true" data-id="element-495" data-mp="3umwljt90wino" />}
          {showError && <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F87171]" aria-hidden="true" data-id="element-496" data-mp="12esh7slw67ry" />}
        </div>
        {showError && <p id={`su-${field}-error`} className="mt-1.5 text-xs font-medium text-[#FCA5A5]" data-id="element-497" data-mp="1c53lxhdegkzw">
            {errors[field]}
          </p>}
      </div>;
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.2,
    ease
  }} className="halloween-theme fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-id="element-498" data-mp="jf4szaw343py">
      <motion.div role="dialog" aria-modal="true" aria-labelledby="event-signup-title" initial={{
      opacity: 0,
      scale: 0.96,
      y: 8
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease
    }} onClick={e => e.stopPropagation()} className="bg-[#1C1917] text-[#F5EDE3] rounded-2xl shadow-2xl w-full max-w-md overflow-hidden my-8 border-t-4 border-[#FF7A1A]" data-id="element-499" data-mp="oerfyc1psyby">
        <div className="px-6 pt-6 pb-5 border-b border-[#F5EDE3]/10 flex items-start justify-between gap-4" data-id="element-500" data-mp="2capakle31uid">
          <div className="min-w-0" data-id="element-501" data-mp="1xhvuoyuwfcd6">
            <p className="text-xs font-bold text-[#FF9A4D] mb-1.5" data-id="element-502" data-mp="22dsyy6sw0s9l">
              Boka in dig · Halloweek
            </p>
            <h2 id="event-signup-title" className="text-2xl font-bold leading-tight" data-id="element-503" data-mp="2q5hv5y8ty0vt">
              {event.title}
            </h2>
            <p className="mt-2 text-sm text-[#F5EDE3]/60 flex items-center gap-1.5" data-id="element-504" data-mp="2gewxqe4w72eh">
              <Clock className="w-3.5 h-3.5 shrink-0" aria-hidden="true" data-id="element-505" data-mp="2ld6x70xngp5w" />
              <span className="capitalize" data-id="element-506" data-mp="2lb3vhjn4x7lc">{dateLabel}</span>
              {event.time ? ` · ${event.time}` : ''}
            </p>
            <p className="mt-1 text-xs font-semibold text-[#FFB27A]" data-id="element-507" data-mp="3duawka8810xt">
              Begränsat antal platser – boka i tid!
            </p>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 -mt-1 text-[#F5EDE3]/50 hover:text-[#F5EDE3] hover:bg-[#F5EDE3]/5 rounded-full transition-colors shrink-0" aria-label="Stäng" data-id="element-508" data-mp="3noyj0nxhdjd4">
            <X className="w-5 h-5" data-id="element-509" data-mp="39pytzf6232gf" />
          </button>
        </div>

        <div className="p-6" data-id="element-510" data-mp="39pp1nkxjgj7k">
          {!sent ? <form onSubmit={handleSubmit} noValidate className="space-y-4" data-id="element-511" data-mp="34r5g3lmux9fq">
              {event.bookingOptions && <fieldset aria-describedby={choiceTouched && choiceError ? 'su-choice-error' : undefined} data-id="element-512" data-mp="2zzdpxc7m55q4">
                  <legend className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-513" data-mp="2r0a7mag9suco">
                    {event.bookingOptions.question} *
                  </legend>
                  <div className="space-y-2" data-id="element-514" data-mp="2h1kxx4yj2g7i">
                    {event.bookingOptions.choices.map((c, i) => {
                const selected = choice === c.label;
                return <label key={c.label} className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors duration-150 ${selected ? 'border-[#FF7A1A] bg-[#FF7A1A]/10' : choiceTouched && choiceError ? 'border-[#F87171] bg-[#0E0C0B]' : 'border-[#F5EDE3]/15 bg-[#0E0C0B] hover:border-[#F5EDE3]/35'}`} data-id="element-515" data-mp="11hu2nib6sc0t" data-mpi={i}>
                          <input id={`su-choice-${i}`} type="radio" name="booking-choice" value={c.label} checked={selected} onChange={() => setChoice(c.label)} className="sr-only peer" data-id="element-516" data-mp="rjxi5zwd04q1" />
                          <span className={`mt-0.5 w-5 h-5 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-[#FF7A1A]/50 ${selected ? 'bg-[#FF7A1A] border-[#FF7A1A]' : 'border-[#F5EDE3]/40'}`} aria-hidden="true" data-id="element-517" data-mp="3762tmli033vy">
                            {selected && <Check className="w-3.5 h-3.5 text-[#1A0F08]" strokeWidth={3} data-id="element-518" data-mp="8luk6j7lhivi" />}
                          </span>
                          <span className="min-w-0" data-id="element-519" data-mp="25gz4hfclj5d0">
                            <span className="block text-sm font-bold text-[#F5EDE3]" data-id="element-520" data-mp="25ijy92r36k9t">
                              {c.label}
                            </span>
                            {c.description && <span className="block text-xs text-[#F5EDE3]/55 mt-0.5" data-id="element-521" data-mp="b8d5b1u57fo2">
                                {c.description}
                              </span>}
                          </span>
                        </label>;
              })}
                  </div>
                  {choiceTouched && choiceError && <p id="su-choice-error" className="mt-1.5 text-xs font-medium text-[#FCA5A5]" data-id="element-522" data-mp="66xip5cbh13k">
                      {choiceError}
                    </p>}
                </fieldset>}
              {partyCfg && <div data-id="element-523" data-mp="1rmld1qa97kfn">
                  <label htmlFor="su-party" className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-524" data-mp="1rmv6xoyf6bqm">
                    {partyCfg.label} *
                  </label>
                  <div className="flex items-center gap-3" data-id="element-525" data-mp="1l4s2bcbbsiwf">
                    <div className={`flex items-center h-12 rounded-lg bg-[#0E0C0B] border transition-[border-color] duration-150 ${partyTouched && partyError ? 'border-[#F87171]' : 'border-[#F5EDE3]/15 focus-within:border-[#FF7A1A]'}`} data-id="element-526" data-mp="1g6qigp62sfjw">
                      <button type="button" onClick={() => stepParty(-1)} disabled={party !== '' && partyNum <= partyCfg.min} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="En person färre" data-id="element-527" data-mp="1z5up5ra59x8e">
                        −
                      </button>
                      <input id="su-party" type="number" inputMode="numeric" min={partyCfg.min} max={partyCfg.max} step={1} value={party} placeholder={String(partyCfg.min)} onChange={e => setParty(e.target.value)} onBlur={() => setPartyTouched(true)} aria-invalid={!!(partyTouched && partyError)} aria-describedby="su-party-hint" className="w-14 h-full bg-transparent text-center text-lg font-bold text-[#F5EDE3] placeholder:text-[#F5EDE3]/25 outline-none tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" data-id="element-528" data-mp="2z5oipkuyhxf1" />
                      <button type="button" onClick={() => stepParty(1)} disabled={party !== '' && partyNum >= partyCfg.max} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="En person till" data-id="element-529" data-mp="qxatnlo0ccwk">
                        +
                      </button>
                    </div>
                    <span className="text-sm text-[#F5EDE3]/60" data-id="element-530" data-mp="3am5ii8eno7i">personer</span>
                  </div>
                  <p id="su-party-hint" className={`mt-1.5 text-xs font-medium ${partyTouched && partyError ? 'text-[#FCA5A5]' : 'text-[#FF9A4D]'}`} data-id="element-531" data-mp="h293qnlwr6sx">
                    {partyTouched && partyError ? partyError : partyCfg.hint}
                  </p>
                </div>}
              {selectedChoice?.note && <p className="rounded-lg bg-[#FF7A1A]/10 border border-[#FF7A1A]/30 px-3 py-2.5 text-sm text-[#F5EDE3]/90 leading-relaxed" data-id="element-532" data-mp="200f3pv68y2rg">
                  {selectedChoice.note}
                </p>}
              {isKidsEvent && <div className="space-y-3" data-id="element-533" data-mp="2ewe92y1z1osw">
                  <button type="button" role="checkbox" aria-checked={asParent} onClick={() => setAsParent(v => !v)} className={`w-full flex items-start gap-3 text-left rounded-xl border px-3 py-3 transition-colors ${asParent ? 'border-[#FF7A1A] bg-[#FF7A1A]/10' : 'border-[#F5EDE3]/15 hover:border-[#F5EDE3]/35'}`} data-id="element-534" data-mp="2exfwm206c3ri">
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${asParent ? 'bg-[#FF7A1A] border-[#FF7A1A]' : 'border-[#F5EDE3]/40'}`} aria-hidden="true" data-id="element-535" data-mp="yfs886aqqhwe">
                      {asParent && <Check className="w-3.5 h-3.5 text-[#1A0F08]" data-id="element-536" data-mp="13r4ikfbuvcvf" />}
                    </span>
                    <span data-id="element-537" data-mp="q0hmkuxhc131">
                      <span className="block text-sm font-bold text-[#F5EDE3]" data-id="element-538" data-mp="pz6isv41atbx">
                        Jag är förälder
                      </span>
                      <span className="block text-xs text-[#F5EDE3]/60 leading-relaxed" data-id="element-539" data-mp="g2v27sxevhbo">
                        Bocka i för att anmäla dina barn – du anger hur många
                        du tar med. Anmäler sig barnet självt, lämna rutan tom.
                      </span>
                    </span>
                  </button>

                  {isParent && <div data-id="element-540" data-mp="c32oz2x2omzz">
                      <label htmlFor="su-children" className="block text-xs font-semibold text-[#F5EDE3]/70 mb-1.5" data-id="element-541" data-mp="3okc7egqc6u6a">
                        Hur många barn tar du med? *
                      </label>
                      <div className="flex items-center gap-3" data-id="element-542" data-mp="1drxwqiogdkj">
                        <div className={`flex items-center h-12 rounded-lg bg-[#0E0C0B] border transition-[border-color] duration-150 ${childTouched && childError ? 'border-[#F87171]' : 'border-[#F5EDE3]/15 focus-within:border-[#FF7A1A]'}`} data-id="element-543" data-mp="3tim6h2f6mex6">
                          <button type="button" onClick={() => stepChildren(-1)} disabled={childNum <= 1} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="Ett barn färre" data-id="element-544" data-mp="g63o5z9pffqs">
                            −
                          </button>
                          <input id="su-children" type="number" inputMode="numeric" min={1} max={MAX_CHILDREN} step={1} value={childCount} onChange={e => setChildCount(e.target.value)} onBlur={() => setChildTouched(true)} aria-invalid={!!(childTouched && childError)} aria-describedby="su-children-hint" className="w-14 h-full bg-transparent text-center text-lg font-bold text-[#F5EDE3] outline-none tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" data-id="element-545" data-mp="3rwtt5swnwr92" />
                          <button type="button" onClick={() => stepChildren(1)} disabled={childNum >= MAX_CHILDREN} className="w-11 h-full text-xl font-bold text-[#F5EDE3]/70 hover:text-[#FF9A4D] disabled:opacity-30 disabled:hover:text-[#F5EDE3]/70 transition-colors" aria-label="Ett barn till" data-id="element-546" data-mp="28y4dlba5bizo">
                            +
                          </button>
                        </div>
                        <span className="text-sm text-[#F5EDE3]/60" data-id="element-547" data-mp="2iq1o0ftby6xc">barn</span>
                      </div>
                      <p id="su-children-hint" className={`mt-1.5 text-xs font-medium ${childTouched && childError ? 'text-[#FCA5A5]' : 'text-[#F5EDE3]/55'}`} data-id="element-548" data-mp="1ca7spu7oe1in">
                        {childTouched && childError ? childError : 'Anmälan görs i ditt namn – fyll i dina uppgifter nedan.'}
                      </p>
                    </div>}
                </div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-549" data-mp="3dxisx5oekeur">
                {renderField('firstName', isParent ? 'Förälderns förnamn *' : 'Förnamn *', {
              type: 'text',
              autoComplete: 'given-name',
              maxLength: 40
            })}
                {renderField('lastName', isParent ? 'Förälderns efternamn *' : 'Efternamn *', {
              type: 'text',
              autoComplete: 'family-name',
              maxLength: 40
            })}
              </div>
              {renderField('email', 'E-post *', {
            type: 'email',
            autoComplete: 'email',
            inputMode: 'email',
            placeholder: 'namn@exempel.se'
          })}
              {renderField('phone', 'Telefon *', {
            type: 'tel',
            autoComplete: 'tel',
            inputMode: 'tel',
            placeholder: '070-123 45 67'
          })}

              {event.formNote && <p className="rounded-lg bg-[#FF7A1A]/10 border border-[#FF7A1A]/30 px-3 py-2.5 text-sm text-[#F5EDE3]/90 leading-relaxed" data-id="element-550" data-mp="1282v1ohkhntl">
                  {event.formNote}
                </p>}

              {error && <p role="alert" className="p-3 bg-[#F87171]/10 text-[#FCA5A5] text-sm rounded-lg border border-[#F87171]/30" data-id="element-551" data-mp="ohr1dl5c3h0c">
                  {error}
                </p>}

              <div className="pt-2" data-id="element-552" data-mp="2jlfbjagxj1vd">
                <button type="submit" disabled={isSubmitting} className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#FF7A1A] text-[#1A0F08] font-bold hover:bg-[#FF9A4D] disabled:opacity-60 disabled:cursor-wait transition-colors" data-id="element-553" data-mp="2okihqy8nbe4x">
                  {isSubmitting && <span className="w-4 h-4 rounded-full border-2 border-[#1A0F08]/30 border-t-[#1A0F08] animate-spin" aria-hidden="true" data-id="element-554" data-mp="20owbrbs0mqbh" />}
                  {isSubmitting ? 'Skickar…' : event.ctaLabel ?? 'Boka min plats'}
                </button>
                <p className="text-center text-xs text-[#F5EDE3]/45 mt-3" data-id="element-555" data-mp="3l24zlaz7zjmd">
                  Vi bekräftar din plats via mejl.
                </p>
              </div>
            </form> : <div className="text-center py-6 space-y-4" data-id="element-556" data-mp="3zsduzdzuzl4">
              <div className="w-14 h-14 bg-[#FF7A1A]/15 rounded-full flex items-center justify-center mx-auto" data-id="element-557" data-mp="3v638odsev440">
                <CheckCircle2 className="w-7 h-7 text-[#FF7A1A]" data-id="element-558" data-mp="3qf3az6bgwi0g" />
              </div>
              <h3 className="text-2xl font-bold" data-id="element-559" data-mp="3met842iz27pt">Tack, {firstName}!</h3>
              <p className="text-[#F5EDE3]/70 max-w-sm mx-auto text-sm leading-relaxed" data-id="element-560" data-mp="3mcgqxrl1g1dt">
                {isParent ? `Vi har tagit emot din anmälan av ${childText} till ${event.title}. Du får en bekräftelse från oss på ${values.email.trim()}.` : `Vi har tagit emot din bokning till ${event.title}. Du får en bekräftelse från oss på ${values.email.trim()}.`}
              </p>
              <div className="pt-4" data-id="element-561" data-mp="15pnnolbgjbfk">
                <button onClick={onClose} className="px-8 py-3 rounded-full border border-[#F5EDE3]/40 text-sm font-bold hover:bg-[#F5EDE3] hover:text-[#0E0C0B] transition-colors" data-id="element-562" data-mp="15omfcoltt31j">
                  Stäng
                </button>
              </div>
            </div>}
        </div>
      </motion.div>
    </motion.div>;
}